/**
 * @module lib/commercial/dfp-ads/slimcut-ad
 * @description Animated Advertisement that appears as an article content inline ad.
 * It may be configured via PB `article/content` feature and placed via backend JSP code.
 * It may be configured via Composer article content item and the placeholder is swapped via this JS file.
 */
import tgamConfig from "config/tgam";
import logger from "utils/logger";
import loadScript from "utils/load-script";
import { qsa, getById, getProp, removeNode } from "utils/utils";
import { isMobile, isTablet, isDesktop } from "utils/detect";

const debugTag = "[SLIMCUT-AD]";

// Use `base` as the template, and replace "PID" with the published id value.
const URLS = {
  base: "https://static.freeskreen.com/publisher/PID/freeskreen.min.js",
  adv: "3622",
  mobile: "230",
  tablet: "2775",
  tabletWide: "3059"
};

/**
 * @method attachSlimcutScript
 * @description Asynchronously attaches Slimcut Advert JS file to the page.
 */
export function attachSlimcutScript() {
  if (!getProp(tgamConfig, "meta.allowSlimcutAd", false)) {
    return;
  }

  const isAdv = getProp(tgamConfig, "meta.advContent", false);
  const isLongRead = getProp(tgamConfig, "meta.isStoryLongReadPage", false);

  let script = getScriptUrl(URLS, isAdv, isLongRead);
  let oldAd = null; // from inline-ads/display.jsp
  let newAds = []; // from Composer content items

  if (isAdv) {
    newAds = qsa(".js-ad--slimcut-adv");
    script = newAds.length ? script : null;
  } else {
    oldAd = getById("js-ad-slimcut-wrapper");
    script = oldAd ? script : null;
  }

  if (!script) {
    logger.info(`${debugTag} abort:`, { isAdv, isLongRead, ads: isAdv ? newAds : oldAd });
    return;
  }

  if (isAdv) {
    // Remove backend placed Ads. Since there should be at least one, manually placed Ad(s).
    removeNode(oldAd);

    // To be replaced with dynamically generated Slimcut Ads.
    newAds = newAds.map((placeholder, i) => {
      const id = `js-ad-slimcut${i === 0 ? "" : i}`;
      placeholder.parentNode.insertAdjacentHTML("afterend", generateSlimcut(id));
      removeNode(placeholder.parentNode); // Remove the placeholder element
      return getById(id);
    });
  }

  logger.info(`${debugTag} attach:`, {
    isAdv,
    isLongRead,
    ads: isAdv ? newAds : oldAd,
    script
  });

  loadScript(script);
}

/**
 * Get the url for the Slimcut script, if applicable for the page.
 * @param {Object} urls - of Slimcut urls
 * @param {Boolean} isAdv - true when article is ADV
 * @param {Boolean} isLongRead - true when article is "longread" sub-type
 * @returns {String|null} of url
 */
function getScriptUrl(urls, isAdv, isLongRead) {
  let pid; // publisher id

  if (isAdv) { // ADV article template
    pid = urls.adv;
  } else if (isLongRead) { // longread article template
    if (isMobile()) {
      pid = urls.mobile;
    } else if (isTablet()) {
      pid = urls.tablet;
    } else {
      pid = urls.tabletWide;
    }
  } else if (isTablet("tabletWide") || isDesktop()) { // standard article template
    pid = urls.tabletWide;
  }

  return pid ? urls.base.replace("PID", pid) : null;
}

/**
 * Generate the HTML markup for a Slimcut Advert.
 * Match Slimcut Ad markup in: features/article/content/components/inline-ads/display.jsp
 * @param {String} id - of ad element
 * @returns {String} of HTML
 */
function generateSlimcut(id = "js-ad-slimcut") {
  return `<div id="js-ad-slimcut-wrapper"><div id="${id}"></div></div>`;
}
